import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Player, CreatePlayerRequest, UpdatePlayerRequest, PlayerAccountOption } from '../../types/player';
import { playersAPI, ageGroupsAPI, schoolsAPI } from '../../services/api';
import AccountPicker from './AccountPicker';
import type { SchoolOption } from '../../types/school';
import AgeGroupSelect from '../common/AgeGroupSelect';
import type { AgeGroupSelect as AgeGroupSelectType, PlayerAgeGroups } from '../../types/ageGroup';
import AiExtractPlayerPanel from '../ai/AiExtractPlayerPanel';
import { useAiStatus } from '../../hooks/useAiStatus';
import { useAuth } from '../../contexts/AuthContext';
import type { AiExtractedPlayer } from '../../types/ai';
import { normalizeTaj, validateTaj } from '../../utils/taj';
import { getErrorMessage } from '../../utils/errorMessage';
import { COUNTRIES } from '../../utils/countries';

interface PlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: Player | null;
  onSave: () => void;
}

const PlayerModal: React.FC<PlayerModalProps> = ({ isOpen, onClose, player, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    ageGroupId: null as number | null,
    ageGroup: '', // Keep for backward compatibility
    birth: '',
    citizen: '',
    countryOfBirth: '',
    gender: '',
    ifaCode: '',
    mothersName: '',
    placeOfBirth: '',
    sportDoctorValidity: '',
    taj: '',
    userId: '',
    parentUserId: '',
    secondParentUserId: '',
    schoolId: '',
    phone: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    medicalNotes: '',
  });
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<PlayerAccountOption[]>([]);
  const [suggestedAgeGroup, setSuggestedAgeGroup] = useState<string | null>(null);
  const [selectedAgeGroupIds, setSelectedAgeGroupIds] = useState<number[]>([]);
  const errorRef = useRef<HTMLDivElement>(null);

  // A hibaüzenet az űrlap tetején van - a lent kattintó felhasználó is lássa
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);
  const [allAgeGroups, setAllAgeGroups] = useState<AgeGroupSelectType[]>([]);

  const genders = ['Férfi', 'Nő'];

  const ai = useAiStatus();
  const { user } = useAuth();
  // Dokumentumból kitöltés: csak új játékosnál, csak adminnak, külön engedéllyel
  const canExtract = !player && ai.personalDataAllowed && !!user?.isAdmin;

  const applyExtracted = (data: AiExtractedPlayer) => {
    const filled = Object.fromEntries(
      (
        [
          ['name', data.name],
          ['birth', data.birth ? data.birth.split('T')[0] : null],
          ['gender', data.gender],
          ['placeOfBirth', data.placeOfBirth],
          ['countryOfBirth', data.countryOfBirth],
          ['citizen', data.citizen],
          ['mothersName', data.mothersName],
          ['taj', data.taj],
          ['address', data.address],
          ['phone', data.phone],
          ['emergencyContactName', data.emergencyContactName],
          ['emergencyContactPhone', data.emergencyContactPhone],
          ['emergencyContactRelation', data.emergencyContactRelation],
        ] as [string, string | null | undefined][]
      ).filter(([, value]) => !!value)
    );
    setFormData((prev) => ({ ...prev, ...filled }));

    // A születési dátumból ugyanúgy jöjjön korosztály-javaslat, mint kézi beírásnál
    const birth = filled.birth as string | undefined;
    if (birth) {
      ageGroupsAPI
        .suggestForBirthDate(birth)
        .then((suggestion) => {
          if (!suggestion) return;
          setSuggestedAgeGroup(suggestion.name);
          setFormData((prev) => (prev.ageGroupId ? prev : { ...prev, ageGroupId: suggestion.id, ageGroup: suggestion.name }));
          setSelectedAgeGroupIds((prev) => (prev.length ? prev : [suggestion.id]));
        })
        .catch(() => undefined);
    }
  };

  /**
   * Dátum a <input type="date"> mezőhöz, helyi naptár szerint.
   * A toISOString() UTC-re vált, ami magyar időzónában egy nappal
   * korábbi dátumot adna - és minden mentésnél tovább csúszna.
   */
  const toDateInputValue = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  // Saját fiókként linkelhető userek (van saját Player rekordjuk)
  const playerUsers = users.filter(u => u.isPlayer);
  // Szülőként linkelhető userek
  const parentUsers = users.filter(u => u.isParent);

  useEffect(() => {
    const init = async () => {
      if (!isOpen) return;

      setError(null);
      setSuggestedAgeGroup(null);

      // Mindig betöltjük a felhasználókat és az összes korosztályt
      loadUsers();
      schoolsAPI.getOptions().then(setSchools).catch((err) => console.error('Failed to load schools:', err));
      await loadAllAgeGroups();

      if (player) {
        setFormData({
          name: player.name,
          ageGroupId: player.ageGroupId || null,
          ageGroup: player.ageGroup,
          birth: toDateInputValue(player.birth),
          citizen: player.citizen || '',
          countryOfBirth: player.countryOfBirth || '',
          gender: player.gender,
          ifaCode: player.ifaCode || '',
          mothersName: player.mothersName || '',
          placeOfBirth: player.placeOfBirth || '',
          sportDoctorValidity: toDateInputValue(player.sportDoctorValidity),
          taj: player.taj || '',
          userId: player.userId?.toString() || '',
          parentUserId: player.parentUserId?.toString() || '',
          secondParentUserId: player.secondParentUserId?.toString() || '',
          schoolId: player.schoolId?.toString() || '',
          phone: player.phone || '',
          address: player.address || '',
          emergencyContactName: player.emergencyContactName || '',
          emergencyContactPhone: player.emergencyContactPhone || '',
          emergencyContactRelation: player.emergencyContactRelation || '',
          medicalNotes: player.medicalNotes || '',
        });

        // Játékos jelenlegi korosztályai (many-to-many)
        try {
          const ageGroups: PlayerAgeGroups = await ageGroupsAPI.getPlayerAgeGroups(player.id);
          const ids = ageGroups.ageGroups.map(ag => ag.id);
          setSelectedAgeGroupIds(ids);
          // formData.ageGroup maradhat legacy-ként; itt nem írjuk felül
        } catch (err) {
          console.error('Failed to load player age groups:', err);
          // Ha a hívás Admin-ra korlátozott, vagy hiba adódik, visszaesünk az egyes ageGroup/ageGroupId-re
          setSelectedAgeGroupIds(player.ageGroupId ? [player.ageGroupId] : []);
        }
      } else {
        setFormData({
          name: '',
          ageGroupId: null,
          ageGroup: '',
          birth: '',
          citizen: '',
          countryOfBirth: '',
          gender: '',
          ifaCode: '',
          mothersName: '',
          placeOfBirth: '',
          sportDoctorValidity: '',
          taj: '',
          userId: '',
          parentUserId: '',
          secondParentUserId: '',
          schoolId: '',
          phone: '',
          address: '',
          emergencyContactName: '',
          emergencyContactPhone: '',
          emergencyContactRelation: '',
          medicalNotes: '',
        });
        setSelectedAgeGroupIds([]);
      }
    };

    init();
  }, [isOpen, player]);

  const loadUsers = async () => {
    try {
      const data = await playersAPI.getAccountOptions();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadAllAgeGroups = async () => {
    try {
      const data = await ageGroupsAPI.getForSelect();
      setAllAgeGroups(data);
    } catch (err) {
      console.error('Failed to load age groups for multi-select:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (selectedAgeGroupIds.length === 0) {
      setError('Legalább egy korosztályt válassz ki.');
      return;
    }

    const tajError = validateTaj(formData.taj);
    if (tajError) {
      setError(tajError);
      return;
    }

    if (formData.birth && new Date(formData.birth) > new Date()) {
      setError('A születési dátum nem lehet a jövőben.');
      return;
    }

    // A régi, egyetlen fő korosztály mindig a kiválasztottak közül való legyen -
    // korábban a születési dátumos javaslat csendben más csapatba tette a játékost
    const primaryId = formData.ageGroupId && selectedAgeGroupIds.includes(formData.ageGroupId)
      ? formData.ageGroupId
      : selectedAgeGroupIds[0];
    const primaryName = allAgeGroups.find((ag) => ag.id === primaryId)?.name ?? formData.ageGroup;

    setLoading(true);
    try {
      const playerData: CreatePlayerRequest | UpdatePlayerRequest = {
        name: formData.name,
        ageGroup: primaryName, // Keep for backward compatibility
        ageGroupId: primaryId,
        birth: formData.birth,
        gender: formData.gender,
        citizen: formData.citizen || undefined,
        countryOfBirth: formData.countryOfBirth || undefined,
        ifaCode: formData.ifaCode || undefined,
        mothersName: formData.mothersName || undefined,
        placeOfBirth: formData.placeOfBirth || undefined,
        sportDoctorValidity: formData.sportDoctorValidity || undefined,
        taj: normalizeTaj(formData.taj) || undefined,
        userId: formData.userId ? parseInt(formData.userId) : undefined,
        parentUserId: formData.parentUserId ? parseInt(formData.parentUserId) : undefined,
        secondParentUserId: formData.secondParentUserId ? parseInt(formData.secondParentUserId) : undefined,
        schoolId: formData.schoolId ? parseInt(formData.schoolId) : undefined,
        // Szerkesztéskor az üres választás törli az iskolát
        ...(player && !formData.schoolId ? { clearSchoolId: true } : {}),
        // ...és a fiókok hozzárendelését is (különben a backend "nem változott"-nak venné)
        ...(player && !formData.userId && player.userId ? { clearUserId: true } : {}),
        ...(player && !formData.parentUserId && player.parentUserId ? { clearParentUserId: true } : {}),
        ...(player && !formData.secondParentUserId && player.secondParentUserId ? { clearSecondParentUserId: true } : {}),
      };

      // Elérhetőségek: létrehozáskor sima mezők, szerkesztéskor teljes csere (üres = törlés)
      const contact = {
        phone: formData.phone,
        address: formData.address,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        emergencyContactRelation: formData.emergencyContactRelation,
        medicalNotes: formData.medicalNotes,
      };
      if (player) {
        (playerData as UpdatePlayerRequest).contact = contact;
      } else {
        Object.assign(playerData, contact);
      }

      if (player) {
        await playersAPI.update(player.id, playerData as UpdatePlayerRequest);
        await syncPlayerAgeGroups(player.id);
      } else {
        const created = await playersAPI.create(playerData as CreatePlayerRequest);
        await syncPlayerAgeGroups(created.id);
      }

      onSave();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Hiba történt a művelet során'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // When birth date changes, suggest an age group
    if (name === 'birth' && value) {
      try {
        const suggestion = await ageGroupsAPI.suggestForBirthDate(value);
        if (suggestion) {
          setSuggestedAgeGroup(suggestion.name);
          // Only auto-set if no age group is selected yet
          if (!formData.ageGroupId) {
            setFormData(prev => ({
              ...prev,
              ageGroupId: suggestion.id,
              ageGroup: suggestion.name,
            }));
          }
          // A javaslat látható módon, bepipálva kerül a korosztályok közé
          setSelectedAgeGroupIds(prev => (prev.length ? prev : [suggestion.id]));
        }
      } catch (error) {
        console.error('Failed to get age group suggestion:', error);
      }
    }
  };

  const handleAgeGroupChange = (ageGroupId: number | null, ageGroupName?: string) => {
    // Single-select handler (legacy, pl. birth-date suggestion)
    setFormData(prev => ({
      ...prev,
      ageGroupId,
      ageGroup: ageGroupName || '',
    }));

    if (ageGroupId) {
      setSelectedAgeGroupIds(prev =>
        prev.includes(ageGroupId) ? prev : [...prev, ageGroupId]
      );
    }
  };

  const toggleAgeGroup = (id: number) => {
    setSelectedAgeGroupIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const syncPlayerAgeGroups = async (playerId: number) => {
    try {
      const current = await ageGroupsAPI.getPlayerAgeGroups(playerId);
      const currentIds = current.ageGroups.map(ag => ag.id);

      const toAdd = selectedAgeGroupIds.filter(id => !currentIds.includes(id));
      const toRemove = currentIds.filter(id => !selectedAgeGroupIds.includes(id));

      // Remove from age groups that are no longer selected
      for (const id of toRemove) {
        await ageGroupsAPI.removePlayer(id, playerId);
      }

      // Add to newly selected age groups
      for (const id of toAdd) {
        await ageGroupsAPI.addPlayer(id, playerId);
      }
    } catch (err) {
      console.error('Failed to sync player age groups:', err);
      // Nem dobjuk tovább, hogy a fő mentés ne bukjon el teljesen
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-w-2xl md:rounded-lg md:max-h-[90vh]">
          <div className="flex shrink-0 items-center justify-between p-4 md:p-6 border-b">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {player ? 'Játékos szerkesztése' : 'Új játékos hozzáadása'}
            </Dialog.Title>
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-600 rounded-full"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6 pb-safe-6">
            {error && (
              <div ref={errorRef} role="alert" className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                {error}
              </div>
            )}

            {canExtract && <AiExtractPlayerPanel onExtracted={applyExtracted} />}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Név */}
              <div className="md:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Név *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Korosztályok (többes választás) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Korosztályok *
                </label>

                {/* Javaslat születési dátum alapján – a legacy single-selecttel összekötve */}
                <div className="mb-2">
                  <AgeGroupSelect
                    value={formData.ageGroupId}
                    onChange={handleAgeGroupChange}
                    required={false}
                    autoSelectSingle={false}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Korosztály hozzáadása (javaslat születési dátum alapján)"
                  />
                  {suggestedAgeGroup && formData.ageGroupId === null && (
                    <p className="mt-1 text-xs text-green-600">
                      Javasolt korosztály a születési dátum alapján: {suggestedAgeGroup}
                    </p>
                  )}
                </div>

                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                  {allAgeGroups.length === 0 ? (
                    <p className="text-xs text-gray-500">Nincsenek elérhető korosztályok.</p>
                  ) : (
                    allAgeGroups.map((ag) => (
                      <label
                        key={ag.id}
                        className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                          checked={selectedAgeGroupIds.includes(ag.id)}
                          onChange={() => toggleAgeGroup(ag.id)}
                        />
                        <span>{ag.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Nem */}
              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                  Nem *
                </label>
                <select
                  id="gender"
                  name="gender"
                  required
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Válassz nemet</option>
                  {genders.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
              </div>

              {/* Születési idő */}
              <div>
                <label htmlFor="birth" className="block text-sm font-medium text-gray-700 mb-2">
                  Születési idő *
                </label>
                <input
                  type="date"
                  id="birth"
                  name="birth"
                  required
                  value={formData.birth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Születési hely */}
              <div>
                <label htmlFor="placeOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Születési hely
                </label>
                <input
                  type="text"
                  id="placeOfBirth"
                  name="placeOfBirth"
                  value={formData.placeOfBirth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Állampolgárság */}
              <div>
                <label htmlFor="citizen" className="block text-sm font-medium text-gray-700 mb-2">
                  Állampolgárság
                </label>
                <input
                  type="text"
                  id="citizen"
                  name="citizen"
                  value={formData.citizen}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Születési ország */}
              <div>
                <label htmlFor="countryOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Születési ország
                </label>
                <select
                  id="countryOfBirth"
                  name="countryOfBirth"
                  value={formData.countryOfBirth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Válassz országot</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              {/* Anyja neve */}
              <div>
                <label htmlFor="mothersName" className="block text-sm font-medium text-gray-700 mb-2">
                  Anyja neve
                </label>
                <input
                  type="text"
                  id="mothersName"
                  name="mothersName"
                  value={formData.mothersName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* TAJ szám */}
              <div>
                <label htmlFor="taj" className="block text-sm font-medium text-gray-700 mb-2">
                  TAJ szám
                </label>
                <input
                  type="text"
                  id="taj"
                  name="taj"
                  value={formData.taj}
                  onChange={handleChange}
                  placeholder="037-687-210"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* IFA kód */}
              <div>
                <label htmlFor="ifaCode" className="block text-sm font-medium text-gray-700 mb-2">
                  IFA kód
                </label>
                <input
                  type="text"
                  id="ifaCode"
                  name="ifaCode"
                  value={formData.ifaCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Sportorvos érvényessége */}
              <div>
                <label htmlFor="sportDoctorValidity" className="block text-sm font-medium text-gray-700 mb-2">
                  Sportorvos érvényessége
                </label>
                <input
                  type="date"
                  id="sportDoctorValidity"
                  name="sportDoctorValidity"
                  value={formData.sportDoctorValidity}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Elérhetőség és egészségügyi adatok - a szülő a profiljából is frissítheti */}
              <div className="md:col-span-2 pt-4 border-t mt-4">
                <h3 className="text-sm font-semibold text-gray-900">Elérhetőség és egészség</h3>
                <p className="mt-1 text-xs text-gray-500">A szülők telefonszáma a saját fiókjukon van; ezeket a mezőket a szülő is frissítheti.</p>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  A játékos telefonszáma
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  maxLength={30}
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Lakcím
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  maxLength={200}
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700 mb-2">
                  Vészhelyzeti kontakt neve
                </label>
                <input
                  type="text"
                  id="emergencyContactName"
                  name="emergencyContactName"
                  maxLength={100}
                  value={formData.emergencyContactName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  Vészhelyzeti kontakt telefonja
                </label>
                <input
                  type="tel"
                  id="emergencyContactPhone"
                  name="emergencyContactPhone"
                  maxLength={30}
                  value={formData.emergencyContactPhone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="emergencyContactRelation" className="block text-sm font-medium text-gray-700 mb-2">
                  Kapcsolat
                </label>
                <input
                  type="text"
                  id="emergencyContactRelation"
                  name="emergencyContactRelation"
                  maxLength={50}
                  value={formData.emergencyContactRelation}
                  onChange={handleChange}
                  placeholder="pl. nagymama"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="medicalNotes" className="block text-sm font-medium text-gray-700 mb-2">
                  Egészségügyi tudnivalók
                </label>
                <textarea
                  id="medicalNotes"
                  name="medicalNotes"
                  rows={2}
                  maxLength={1000}
                  value={formData.medicalNotes}
                  onChange={handleChange}
                  placeholder="Allergia, asztma, rendszeres gyógyszer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* User account assignment section */}
              <div className="md:col-span-2 pt-4 border-t mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Felhasználói fiókok hozzárendelése</h3>
              </div>

              {/* Játékos fiók */}
              <div>
                <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
                  Játékos fiók
                </label>
                <AccountPicker
                  id="userId"
                  options={playerUsers}
                  value={formData.userId}
                  onChange={(v) => setFormData((prev) => ({ ...prev, userId: v }))}
                />
              </div>

              {/* Első szülő */}
              <div>
                <label htmlFor="parentUserId" className="block text-sm font-medium text-gray-700 mb-2">
                  Első szülő
                </label>
                <AccountPicker
                  id="parentUserId"
                  options={parentUsers}
                  value={formData.parentUserId}
                  onChange={(v) => setFormData((prev) => ({ ...prev, parentUserId: v }))}
                  excludeIds={[formData.secondParentUserId].filter(Boolean)}
                />
              </div>

              {/* Második szülő */}
              <div>
                <label htmlFor="secondParentUserId" className="block text-sm font-medium text-gray-700 mb-2">
                  Második szülő
                </label>
                <AccountPicker
                  id="secondParentUserId"
                  options={parentUsers}
                  value={formData.secondParentUserId}
                  onChange={(v) => setFormData((prev) => ({ ...prev, secondParentUserId: v }))}
                  excludeIds={[formData.parentUserId].filter(Boolean)}
                />
              </div>

              {/* Iskola: ide lehet küldeni a kikérőket, igazolásokat */}
              <div>
                <label htmlFor="schoolId" className="block text-sm font-medium text-gray-700 mb-2">
                  Iskola
                </label>
                <select
                  id="schoolId"
                  name="schoolId"
                  value={formData.schoolId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Nincs megadva</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.hasEmail ? '' : ' (nincs e-mail cím)'}
                    </option>
                  ))}
                </select>
                {schools.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">Iskolát a Klub kezelése → Iskolák menüben lehet felvenni.</p>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col-reverse gap-2 mt-6 pt-6 border-t md:flex-row md:justify-end md:gap-3">
              <button
                type="button"
                onClick={onClose}
                className="w-full md:w-auto px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Mégse
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Mentés...' : (player ? 'Módosítás' : 'Hozzáadás')}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default PlayerModal;